import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import IDScan from 'components/IDScan';
import { convertBase64ToImage } from 'utils/image';
import { securityWaitingMessage } from 'utils/securityWaitingMessage';
import {
  newCheckInAction,
  newNextAction,
  uploadImageToPreSignedUrlAction,
  NEW_NEXT,
} from '../../actions/navigation';
import ContentWrapper from '../../components/ContentWrapper';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Footer from '../../components/Footer';
import Pagination from '../../components/Pagination';
import Header from '../../components/Header';
import Tooltip from '../../components/Tooltip';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Spinner from '../../components/Spinner';
import Headline from '../../components/Headline';
import Container from '../../components/Container';
import Transition from '../../components/Transition';
import Modal from '../../components/Modal';
import ApplicationId from '../../components/ApplicationId';
// Configuration & Assets
import { useConfig } from '../../config/ConfigContext';
import schema from './securitySchema';
import imgError from '../../assets/images/error.svg';

let idScanSocket = null;
let timeoutTimer = null;
const NORMAL_CLOSE = 'Normal close';
let securityWaitingMessageTimer = null;

const Security = (props) => {
  const config = useConfig();
  const { navigate, checkinData, params, searchParams } = props;
  const workflowType = get(params, 'workflowType');
  const key = searchParams.get('key');
  const test = searchParams.get('test');
  const [firstName, setFirstName] = useState(null);
  const [idScanUploadFallback, setIdScanUploadFallback] = useState(false);
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isSecurityModalVisible, setIsSecurityModalVisible] = useState(false);
  const [isDisabledVerifyButton, setIsDisabledVerifyButton] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [IDScanData, setIDScanData] = useState(null);
  const [securityWaitingMessageStep, setSecurityWaitingMessageStep] = useState(null);

  const getBlackbox = () => {
    if (!window.IGLOO || typeof window.IGLOO.getBlackbox !== 'function') {
      console.log('❌ IGLOO not ready — run a collect first');
      return;
    }
    const result = window.IGLOO.getBlackbox();
    console.log('result.blackbox:', result.blackbox);
    return result.blackbox || '';
  };

  useEffect(() => {
    if (checkinData) {
      setFirstName(get(checkinData, 'data.data.firstName'));
    }

    const sourceData = get(checkinData, ['data', 'data']);
    if (sourceData === undefined) {
      if (test) {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}&test=${test}`);
      } else {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
      }
    } else if (get(checkinData, ['data', 'data', 'message', 'text'])) {
      setErrorMessage(get(checkinData, ['data', 'data', 'message', 'text']));
    }

    if (document && document.body && !document.getElementById('ioBlackBox')) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.id = 'ioBlackBox';
      hidden.name = 'ioBlackBox';
      document.body.appendChild(hidden);
    }

    window.io_global_object_name = 'IGLOO';
    window.IGLOO = {
      loader: {
        uri_hook: '/iojs/',
        version: 'general5',
        subkey: '',
      },
    };
    const url = '/assets/js/iovation_loader_only.js';

    const script = document.createElement('script');
    script.async = true;
    script.src = url;

    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const { fetch } = props;

    if (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`]) {
      const routeUrl = checkinData.data && checkinData.data.url.replace(config.hostUrl, '');
      navigate(routeUrl);
    }
  }, [checkinData]);

  useEffect(() => {
    if (securityWaitingMessageStep !== null) {
      securityWaitingMessageTimer = setInterval(() => {
        setSecurityWaitingMessageStep((securityWaitingMessageStep + 1) % 7);
      }, 15000);
    }

    return () => {
      clearInterval(securityWaitingMessageTimer);
    };
  }, [securityWaitingMessageStep]);

  const handleIDScanSubmit = (data) => {
    setIDScanData(data.IDScanData);
    setIsSecurityModalVisible(true);
  };

  const lengthInUtf8Bytes = (str) => {
    // Matches only the 10.. bytes that are non-initial characters in a multi-byte sequence.
    const m = encodeURIComponent(str).match(/%[89ABab]/g);
    return str.length + (m ? m.length : 0);
  };

  const handleFail = (err) => {
    idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
    idScanSocket = null;
    timeoutTimer && clearTimeout(timeoutTimer);
    setErrorMessage(err.message || 'An unexpected error occurred, please try again');
    setIsProgressVisible(false);
    // Enable manual upload fallback for front/back on submission failure
    setIdScanUploadFallback(true);
  };

  const endVerificationProcess = () => {
    const {
      dispatch,
      checkinData: { type },
    } = props;
    idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
    idScanSocket = null;

    dispatch(newCheckInAction({
      key,
      workflowType: type,
      data: { identityStatus: true },
      success: (res) => {
        const isProcessing = !get(res, ['data', 'data', 'isReady']);
        if (!isProcessing) {
          dispatch(newNextAction({
            key,
            workflowType: type,
            data: { identityVerified: true },
            success: (response) => {
              if (get(response, ['data', 'data', 'message', 'text']) || response.isReady) {
                setErrorMessage(get(response, ['data', 'data', 'message', 'text']));
                setIsProgressVisible(false);
              }
            },
            fail: handleFail,
          }));
        } else {
          handleFail({
            message: 'An unexpected error occurred, please try again',
          });
        }
      },
      fail: (e) => {
        handleFail(e);
      },
    }));
  };

  const listenVerificationRes = () => {
    const {
      dispatch,
      checkinData: { type },
    } = props;
    const appId = props.checkinData.attributes.entityId;

    idScanSocket = new WebSocket(`${config.idScanWebsocketUrl}?application_id=${appId}`);
    idScanSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'IDSCAN') {
        dispatch(newCheckInAction({
          key,
          workflowType: type,
          data: { identityStatus: true },
          success: async (res) => {
            const isProcessing = !get(res, ['data', 'data', 'isReady']);
            if (!isProcessing) {
              idScanSocket && idScanSocket.close(1000, NORMAL_CLOSE);
              idScanSocket = null;
              timeoutTimer && clearTimeout(timeoutTimer);
              const iovationBlackBox = await getBlackbox();
              dispatch(newNextAction({
                key,
                workflowType: type,
                data: { identityVerified: true, iovationBlackBox: iovationBlackBox || '' },
                success: (response) => {
                  if (get(response, ['data', 'data', 'message', 'text']) || response.isReady) {
                    if (get(response, ['data', 'data', 'message', 'text'])) {
                      setErrorMessage(get(response, ['data', 'data', 'message', 'text']));
                      setIsProgressVisible(false);
                    }
                  }
                },
                fail: handleFail,
              }));
            }
          },
          fail: (e) => {
            handleFail(e);
          },
        }));
      }
    });
  };

  const callNextWorkflow = () => {
    const {
      dispatch,
      checkinData: {
        type,
        data: {
          data: {
            frontImagePreSignedUrl,
            backOrSecondImagePreSignedUrl,
            selfieImagePreSignedUrl,
          },
        },
      },
    } = props;

    const formData = {
      ...IDScanData,
      countryCode: 'USA',
      verifyFace: true,
    };
    // log the image's size.

    if (test) {
      formData.test = test;
    }

    setIsProgressVisible(true);
    setSecurityWaitingMessageStep(0);

    console.log('IDScan Request: payload size = ', lengthInUtf8Bytes(JSON.stringify(formData)));

    convertBase64ToImage(`data:image/png;base64,${formData.frontImageBase64}`, (blob) => {
      const frontFile = new File([blob], 'front-image', { type: 'image/png' });
      dispatch(uploadImageToPreSignedUrlAction({
        data: {
          url: frontImagePreSignedUrl,
          imageData: frontFile,
        },
        success: () => {
          convertBase64ToImage(`data:image/png;base64,${formData.backOrSecondImageBase64}`, (blob1) => {
            const backFile = new File([blob1], 'back-image', { type: 'image/png' });
            dispatch(uploadImageToPreSignedUrlAction({
              data: {
                url: backOrSecondImagePreSignedUrl,
                imageData: backFile,
              },
              success: () => {
                convertBase64ToImage(`data:image/png;base64,${formData.faceImageBase64}`, (blob2) => {
                  const faceFile = new File([blob2], 'face-image', { type: 'image/png' });
                  dispatch(uploadImageToPreSignedUrlAction({
                    data: {
                      url: selfieImagePreSignedUrl,
                      imageData: faceFile,
                    },
                    success: () => {
                      const IDScanReqData = {
                        frontImagePreSignedUrl,
                        backOrSecondImagePreSignedUrl,
                        selfieImagePreSignedUrl,
                        countryCode: 'USA',
                        verifyFace: true,
                        documentType: formData.documentType,
                        trackString: formData.trackString || null,
                        captureMethod: formData.captureMethod,
                      };
                      dispatch(newNextAction({
                        key,
                        workflowType: type,
                        data: IDScanReqData,
                        success: (res) => {
                          const isProcessing = !get(res, ['data', 'data', 'isReady']);
                          const message = get(res, ['data', 'data', 'message', 'text']);
                          if (!isProcessing || message) {
                            if (message) {
                              setErrorMessage(message);
                              setIsProgressVisible(false);
                            }
                          }
                          if (isProcessing) {
                            timeoutTimer = setTimeout(() => {
                              endVerificationProcess();
                            }, config.verificationIntervals);
                            listenVerificationRes();
                          }
                        },
                        fail: handleFail,
                      }));
                    },
                    fail: () => handleFail({ message: 'Unfortunately, we were not able to upload your selfie. Please try again.' }),
                  }));
                });
              },
              fail: () => handleFail({ message: 'Unfortunately, we were not able to upload the back of your driver’s license. Please try again.' }),
            }));
          });
        },
        fail: () => handleFail({ message: 'Unfortunately, we were not able to upload the front of your driver’s license. Please try again.' }),
      }));
    });
  };

  const handleClose = (e) => {
    e.preventDefault();
    setIsSecurityModalVisible(false);
  };

  const handleAgreeButtonClick = (e) => {
    e.preventDefault();
    setIsSecurityModalVisible(false);

    callNextWorkflow();
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    setIsSecurityModalVisible(true);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    setErrorMessage('');
  };

  const reset = () => {
    setIDScanData(null);
    setIsDisabledVerifyButton(true);
  };

  const disableVerifyBtn = () => {
    setIsDisabledVerifyButton(true);
  };

  return (
    <Transition className="transition-item wrapper">
      <Header />
      <ContentWrapper>
        <ApplicationId
          id={get(props.checkinData, 'attributes.entityId')}
          inHeadline
        />
        <Headline
          appIdVisible
          header={(
            <>
              <span>We take security seriously.</span>
              {' '}
              Upload an ID to verify identity.
              <Tooltip>We adhere to 128-bit SSL protection and strict encryption.</Tooltip>
            </>
          )}
        />
        <Container className="container">
          <IDScan
            handleSubmit={handleIDScanSubmit}
            onReset={reset}
            className={`${
              (
                isSecurityModalVisible
                || !!errorMessage
              ) ? 'hidden-uploader-alert-section ' : ''
            }${
              isProgressVisible ? 'invisible' : 'visible'
            }`}
            onRetakeHook={disableVerifyBtn}
            enableUploadFallback={idScanUploadFallback}
          />
        </Container>
      </ContentWrapper>
      <Footer>
        <FormRow>
          <FormColumn>
            {
              !isProgressVisible
                && (
                <Button
                  className={`btn arrow ${!isDisabledVerifyButton ? 'visibility-hidden' : ''}`}
                  title="Verify Identity"
                  onClick={handleSubmitFrom}
                  isDisabled={isDisabledVerifyButton}
                />
                )
            }
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn>
            <Pagination activeIndex={2} />
          </FormColumn>
        </FormRow>
        {
          !isDisabledVerifyButton && !isProgressVisible && (
            <div className="id-verify-btn-wrapper">
              <Button
                className="btn arrow id-verify-btn"
                title="Verify Identity"
                onClick={handleSubmitFrom}
              />
            </div>
          )
        }
        {
          errorMessage
            && (
            <Modal>
              <div className="card-content error-modal security-error">
                <img src={imgError} alt="" />
                <p>{errorMessage}</p>
                <div onClick={handleButtonClick} className="btn btn-red">OK</div>
              </div>
            </Modal>
            )
        }
        {
          isSecurityModalVisible
            && (
            <Modal className="security-modal">
              <div className="card-header">
                <button className="close" aria-label="Close" onClick={handleClose} type="button" />
                <h5>Consent for ID Verification</h5>
              </div>
              <div className="card-content error-modal" style={{ padding: '30px !important' }}>
                <div onClick={handleAgreeButtonClick} className="btn btn-primary">I Agree</div>
                <p>
                  By clicking &quot;I Agree&quot; you give LendingUSA permission via your signature to scan your ID and to share your information with
                  {' '}
                  <a href="http://idscan.net/">IDScan.net</a>
                  , a third-party fraud risk company who uses the information you provided to us to verify your identity and to help prevent ID theft and fraud, and with any State&apos;s Department of Transportation or equivalent agency.
                </p>
              </div>
            </Modal>
            )
        }
      </Footer>
      {
        isProgressVisible && <Spinner message={securityWaitingMessage(securityWaitingMessageStep, firstName)} />
      }
    </Transition>
  );
};

Security.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
});

export default Validator(schema)(connect(mapStateToProps)(Security));
